import React from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { feedUrl, keepQueryString, marketsUrl, leaderboardUrl } from '@/urls'
import { i18n } from '@/locale/i18n'
import { GlobeIcon, RankingIcon } from '@/assets/icons'
import FutuurLogo from '@/assets/icons/FutuurLogo'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import './Navbar.scss'

/**
 * Navbar loading state: real logo, nav links, and search; skeleton only on the right (user-related data).
 * Use for NavbarPlaceholder (before Navbar mounts) and when Navbar is loading (appLayout.appLoading).
 */
function ShimmerEffect({ className = '', children }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-3/30 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)',
        }}
      />
    </div>
  )
}

export default function NavbarSkeleton() {
  const location = useLocation()
  // Tablets (< lg 1024) get the mobile navbar
  const isMobileSize = useIsMobileSize(1023)
  const isCurrentPage = (path) => {
    if (path === marketsUrl()) {
      return location.pathname === '/' || location.pathname.startsWith('/markets') || location.pathname.startsWith('/tags')
    }
    return location.pathname.startsWith(path)
  }

  return (
    <div className={classNames('navbar', 'navbar--guest')}>
      <div className="navbar__container">
        <div className="navbar__wrap">
          <div className="flex flex-row gap-10 items-center justify-between">
            {/* Logo – real, no loading state */}
            <Link to={feedUrl()} aria-label={i18n('footer.logo_home_link')} className="navbar__flex-col navbar__flex-col-left cursor-pointer">
              <FutuurLogo className="navbar__logo lg:hidden!" width="100px" showName={false} />
            </Link>

            {/* Nav links – real Markets & Leaderboard (no Portfolio until user is known) */}
            <div className="navbar__link-list">
              <NavLink
                className={classNames('navbar__link', { 'navbar__link--active': isCurrentPage(marketsUrl()) })}
                to={keepQueryString(feedUrl())}
              >
                <GlobeIcon width="18px" />
                <span className="navbar__link-text">
                  <div>{i18n('navbar.markets')}</div>
                </span>
              </NavLink>
              <NavLink
                className={classNames('navbar__link', { 'navbar__link--active': isCurrentPage(leaderboardUrl()) })}
                to={keepQueryString(leaderboardUrl())}
              >
                <RankingIcon height="18px" />
                <span className="navbar__link-text">{i18n('navbar.leaderboard')}</span>
              </NavLink>
            </div>

            {/* Search – skeleton while page is loading (desktop) */}
            {!isMobileSize && (
              <div className="navbar__search">
                <ShimmerEffect className="rounded-lg w-full h-full">
                  <div className="h-[44px] w-full bg-surface-3 rounded-lg animate-pulse" />
                </ShimmerEffect>
              </div>
            )}

            {/* Right: skeleton only for user-related data (currency, deposit, bell, menu) */}
            <div className="navbar__flex-col navbar__flex-col--right pr-0 w-full lg:!w-fit flex items-center justify-end gap-2">
              <ShimmerEffect>
                <div className="h-10 w-[117px] bg-surface-3 rounded-lg animate-pulse" />
              </ShimmerEffect>
              {!isMobileSize && (
                <ShimmerEffect className="rounded-lg">
                  <div className="h-[48px] w-[105px] bg-surface-3 animate-pulse" />
                </ShimmerEffect>
              )}
              <ShimmerEffect className="rounded-full">
                <div className="w-[34px] h-[34px] bg-surface-3 rounded-full animate-pulse" />
              </ShimmerEffect>
              <ShimmerEffect className="rounded-lg">
                <div className="w-12 h-12 bg-surface-3 rounded-lg animate-pulse" />
              </ShimmerEffect>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
